import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA, TlkModalWindowModule } from '@itero/ui-toolkit-angular/modal-window';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { CloseEmptyIconComponent } from '@itero/ui-toolkit-angular/icons';
import { ImplantService } from '@modules/tooth-editor/services/implant.service';
import { IdName } from '@shared/models/id-name';
import { Implant } from '@shared/models/implant';
import { Maybe } from '@/types/globals';
import { getSortedListByName } from '@shared/services/sort-list-by-name-util';
import { BaseDestroyableDirective } from '@shared/base-classes/base-destroyable';
import { RoleTypeEnum } from '@shared/models/role-type';
import { FavoritesEnum } from '@shared/models/enums/favorites.enum';
import { FavoriteSelectorComponent } from '@shared/components/favorite-selector/favorite-selector.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ShellQuery } from '@shared/store/shell/shell-query';

import { IntuitiveToothForDiagram } from '../../models/intuitive-tooth-for-diagram';
import { RxIntuitiveMiniTeethDiagramComponent } from '../rx-intuitive-mini-teeth-diagram/rx-intuitive-mini-teeth-diagram.component';
import { ToothModalResult, TOOTH_MODAL_ACTION } from '@modules/rx-intuitive-teeth-diagram/models/tooth-modal';

export interface RxIntuitiveImplantBasedDentureModalData {
	teeth: IntuitiveToothForDiagram[];
	initialImplantTypeId: number | null;
}

export interface RxIntuitiveImplantBasedDentureModalPayload {
	toothPartialUpdate: {
		ImplantTypeID: number;
	} | null;
}

export type RxIntuitiveImplantBasedDentureModalResult = ToothModalResult<RxIntuitiveImplantBasedDentureModalPayload>;

interface ImplantBasedDentureModalForm {
	scanBodyManufacturer: Maybe<IdName>;
	implantManufacturer: Maybe<IdName>;
	implantTypeScanBody: Maybe<IdName>;
}

@Component({
	selector: 'rx-intuitive-implant-based-denture-modal',
	templateUrl: './rx-intuitive-implant-based-denture-modal.component.html',
	styleUrls: ['./rx-intuitive-implant-based-denture-modal.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		AsyncPipe,
		ReactiveFormsModule,
		TranslateModule,
		TlkModalWindowModule,
		TlkButtonComponent,
		CloseEmptyIconComponent,
		RxIntuitiveMiniTeethDiagramComponent,
		FavoriteSelectorComponent,
		SelectComponent
	]
})
export class RxIntuitiveImplantBasedDentureModalComponent extends BaseDestroyableDirective implements OnInit {
	readonly teeth: IntuitiveToothForDiagram[];

	implantManufacturerOptions: IdName[] = [];
	implantTypeScanBodyOptions: Implant[] = [];

	scanBodyManufacturerOptions$: Observable<IdName[]> = this.implantService.scanBodyManufacturerOptions$.pipe(
		map(scanBodyManufacturers => getSortedListByName(this.implantService.filterActiveScanBodyManufacturers({ scanBodyManufacturers })))
	);

	readonly shouldValidateForSend$ = this.shellQuery.shouldValidateForSend$;

	isFavoritesHidden$: Observable<boolean> = this.shellQuery.userRole$.pipe(
		map(userRole => userRole !== RoleTypeEnum.Doctor || !this.shellQuery.isProcedureFlow),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	favoriteScanBody = FavoritesEnum.ScanBodies;
	favoriteImplant = FavoritesEnum.Implants;

	manufactureForm = this.formBuilder.group<ImplantBasedDentureModalForm>({
		scanBodyManufacturer: null,
		implantManufacturer: null,
		implantTypeScanBody: null
	});

	get isProcedureFlow(): boolean {
		return this.shellQuery.isProcedureFlow;
	}

	get scanBodyManufacturerControl() {
		return this.manufactureForm.controls.scanBodyManufacturer;
	}

	get implantManufacturerControl() {
		return this.manufactureForm.controls.implantManufacturer;
	}

	get implantTypeScanBodyControl() {
		return this.manufactureForm.controls.implantTypeScanBody;
	}

	constructor(
		private readonly dialogRef: TlkModalWindowRef<
			RxIntuitiveImplantBasedDentureModalResult,
			RxIntuitiveImplantBasedDentureModalComponent
		>,
		@Inject(TLK_MODAL_WINDOW_DATA) public readonly data: RxIntuitiveImplantBasedDentureModalData,
		private readonly formBuilder: FormBuilder,
		private readonly implantService: ImplantService,
		private readonly shellQuery: ShellQuery,
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		super();
		this.teeth = data.teeth;
	}

	ngOnInit(): void {
		this.initForm();
		this.initControlSubscriptions();
	}

	handleCancel(): void {
		this.dialogRef.close({ action: TOOTH_MODAL_ACTION.Cancel, payload: { toothPartialUpdate: null } });
	}

	handleClose(): void {
		const implantTypeId = this.implantTypeScanBodyControl.value?.Id ?? 0;

		this.dialogRef.close({ action: TOOTH_MODAL_ACTION.Save, payload: { toothPartialUpdate: { ImplantTypeID: implantTypeId } } });
	}

	private initForm(): void {
		const initialId = this.data.initialImplantTypeId;

		if (initialId) {
			const { scanBodyManufacturer, implantManufacturer, implantTypeScanBody } =
				this.implantService.getSelectedValuesByImplantId(initialId);

			this.implantManufacturerOptions = this.implantService.filterImplantManufacturers(scanBodyManufacturer);
			this.implantTypeScanBodyOptions = this.implantService.filterImplants(scanBodyManufacturer, implantManufacturer);

			this.manufactureForm.patchValue({ scanBodyManufacturer, implantManufacturer, implantTypeScanBody }, { emitEvent: false });
		} else {
			this.implantManufacturerOptions = [];
			this.implantTypeScanBodyOptions = [];
			this.manufactureForm.patchValue(
				{ scanBodyManufacturer: null, implantManufacturer: null, implantTypeScanBody: null },
				{ emitEvent: false }
			);
		}

		this.changeDetectorRef.markForCheck();
	}

	private initControlSubscriptions(): void {
		this.scanBodyManufacturerControl.valueChanges
			.pipe(
				tap(scanBodyMfg => {
					this.implantManufacturerOptions = this.implantService.filterImplantManufacturers(scanBodyMfg);

					const implantMfg = this.implantManufacturerControl.value;

					if (this.implantManufacturerOptions?.includes(implantMfg as IdName)) {
						this.implantTypeScanBodyOptions = this.implantService.filterImplants(scanBodyMfg, implantMfg);

						if (!this.implantTypeScanBodyOptions?.includes(this.implantTypeScanBodyControl.value as Implant)) {
							this.implantTypeScanBodyControl.patchValue(null);
						}
					} else {
						this.implantManufacturerControl.patchValue(null);
					}

					this.changeDetectorRef.markForCheck();
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.implantManufacturerControl.valueChanges
			.pipe(
				tap(implantMfg => {
					this.implantTypeScanBodyOptions = this.implantService.filterImplants(
						this.scanBodyManufacturerControl.value as IdName,
						implantMfg as IdName
					);

					if (!this.implantTypeScanBodyOptions?.includes(this.implantTypeScanBodyControl.value as Implant)) {
						this.implantTypeScanBodyControl.patchValue(null);
					}

					this.changeDetectorRef.markForCheck();
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
